import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Ban, Check, Plus } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import type { Partida } from "@/componentes/resumen/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { normalizarDecimal, paraElServidor } from "@/lib/decimales"
import { comoFecha, hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

const TIPO: Record<string, string> = {
  TRANSITO: "En tránsito",
  EFECTIVO: "Efectivo por recibir",
}

/**
 * Lo que viene y aún no está en cuenta (borrador de Andrea, 22/10): «línea separada, con
 * fecha esperada, nunca como saldo en cuenta». Al llegar se marca recibido —y el dinero
 * se registra en su cuenta como siempre—; si no llega, se anula.
 */
export function ListaFondos({ partidas }: { partidas: Partida[] }) {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "conciliacion:registrar")
  const [nuevo, setNuevo] = useState(false)
  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["resumen-macro"] })
  const cerrar = useMutation({
    mutationFn: ({ id, accion }: { id: number; accion: "recibido" | "anular" }) =>
      api.post(`/api/v1/resumen-macro/fondos/${id}/${accion}`),
    onSuccess: refrescar,
  })

  return (
    <div className="space-y-2">
      {puede && (
        <div className="flex justify-end">
          <Boton tamano="sm" onClick={() => setNuevo(true)}>
            <Plus size={14} aria-hidden={true} /> Registrar fondo por recibir
          </Boton>
        </div>
      )}
      <Tabla etiqueta="En tránsito y por recibir">
        <Cabecera>
          <Th>Tipo</Th>
          <Th>Concepto</Th>
          <Th>Se espera</Th>
          <Th derecha>Monto</Th>
          <Th derecha>USD</Th>
          <Th>
            <span className="sr-only">Acciones</span>
          </Th>
        </Cabecera>
        <Cuerpo>
          {partidas.length === 0 && (
            <TablaVacia colSpan={6}>No hay dinero en tránsito ni por recibir.</TablaVacia>
          )}
          {partidas.map((p) => (
            <Fila key={p.ref_id}>
              <Td>{TIPO[p.clase] ?? p.clase}</Td>
              <Td>
                {p.titulo}
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {p.detalle}
                </span>
              </Td>
              <Td numero>{p.fecha ? comoFecha(p.fecha) : "—"}</Td>
              <Td derecha numero>
                {monto(p.monto)} {p.moneda}
              </Td>
              <Td derecha numero>
                {p.usd === null ? "sin tasa" : `$ ${monto(p.usd)}`}
              </Td>
              <Td>
                {puede && p.ref_id !== null && (
                  <span className="flex justify-end gap-1">
                    <Boton
                      tamano="sm"
                      disabled={cerrar.isPending}
                      onClick={() => cerrar.mutate({ id: p.ref_id ?? 0, accion: "recibido" })}
                    >
                      <Check size={14} aria-hidden={true} /> Recibido
                    </Boton>
                    <Boton
                      tamano="sm"
                      variante="fantasma"
                      aria-label={`Anular ${p.titulo}`}
                      disabled={cerrar.isPending}
                      onClick={() => cerrar.mutate({ id: p.ref_id ?? 0, accion: "anular" })}
                    >
                      <Ban size={14} aria-hidden={true} />
                    </Boton>
                  </span>
                )}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
      {nuevo && <DialogoFondo onCerrar={() => setNuevo(false)} onGuardado={refrescar} />}
    </div>
  )
}

function DialogoFondo({ onCerrar, onGuardado }: { onCerrar: () => void; onGuardado: () => void }) {
  const [tipo, setTipo] = useState("TRANSITO")
  const [concepto, setConcepto] = useState("")
  const [quien, setQuien] = useState("")
  const [valor, setValor] = useState("")
  const [moneda, setMoneda] = useState("USD")
  const [fecha, setFecha] = useState(hoyLocal())
  const listo = concepto.trim().length >= 3 && normalizarDecimal(valor) !== null && fecha !== ""
  const guardar = useMutation({
    mutationFn: () =>
      api.post("/api/v1/resumen-macro/fondos", {
        tipo,
        concepto: concepto.trim(),
        quien: quien.trim() || null,
        monto: paraElServidor(valor),
        moneda,
        fecha_esperada: fecha,
      }),
    onSuccess: () => {
      onGuardado()
      onCerrar()
    },
  })
  return (
    <Modal
      abierto={true}
      titulo="Fondo por recibir"
      descripcion="Dinero que viene y aún no está en ninguna cuenta: suma aparte, con su fecha."
      onCerrar={onCerrar}
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (listo) guardar.mutate()
        }}
      >
        <label className="block text-sm">
          Tipo
          <select className="campo mt-1" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="TRANSITO">En tránsito (un cambio que hace un tercero)</option>
            <option value="EFECTIVO">Efectivo por recibir</option>
          </select>
        </label>
        <label className="block text-sm">
          Concepto
          <input
            className="campo mt-1"
            value={concepto}
            maxLength={200}
            placeholder="Ej.: cambio Bs a USD · Calzados Mega"
            onChange={(e) => setConcepto(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          Quién lo trae (opcional)
          <input
            className="campo mt-1"
            value={quien}
            maxLength={120}
            onChange={(e) => setQuien(e.target.value)}
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            Monto
            <input
              className="campo mt-1 tabular-nums"
              inputMode="decimal"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Moneda
            <select
              className="campo mt-1"
              value={moneda}
              onChange={(e) => setMoneda(e.target.value)}
            >
              <option>USD</option>
              <option>USDT</option>
              <option>BS</option>
              <option>EUR</option>
            </select>
          </label>
        </div>
        <label className="block text-sm">
          Fecha esperada
          <input
            type="date"
            className="campo mt-1"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </label>
        {guardar.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" type="submit" disabled={!listo || guardar.isPending}>
            Registrar
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
